/**
 * Email Campaigns — admin composes an HTML email tied to an upcoming
 * live trivia, picks an audience (all verified, or dormant N days),
 * previews, and sends. Once sent, the same screen surfaces delivery
 * status + open-tracking telemetry for the campaign.
 *
 * Two views, dispatched by ctx.campaignId:
 *   - list  (no id)     : paginated table + "New campaign" button
 *   - compose/detail    : form + preview pane + audience preview panel
 *                         with optional per-user selection + (for sent
 *                         campaigns) delivery + open table
 *
 * Preview HTML is rendered in a sandboxed iframe so email styles never
 * leak into the admin page.
 */

import { api, ApiError } from '../../api.js';
import { auth } from '../../auth.js';
import { toast } from '../../components/toast.js';
import {
  errorState,
  escape,
  fmtInt,
  fmtKobo,
  fmtRelTime,
  mountSortableTable,
  skeletonGrid,
} from './common.js';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '';

export async function renderCampaigns(host, ctx) {
  host.innerHTML = skeletonGrid(3);
  try {
    if (ctx.campaignId) {
      await _renderComposer(host, ctx);
    } else {
      await _renderList(host, ctx);
    }
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      ctx.navigate('/login');
      return;
    }
    toast.fromError(err);
    host.innerHTML = errorState(err);
  }
}

// ---------------------------------------------------------------------------
// LIST VIEW — paginated table + New campaign button
// ---------------------------------------------------------------------------

async function _renderList(host, ctx) {
  const page = await api('/admin/campaigns?limit=100&offset=0');
  host.innerHTML = `
    <div style="display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:12px;margin-bottom:18px;">
      <div style="font:500 12px 'IBM Plex Mono',monospace;color:var(--a-muted);">
        ${escape(fmtInt(page.total))} campaigns
      </div>
      <button id="camp-new" class="adm2-btn primary" type="button">＋ New campaign</button>
    </div>
    <div class="adm2-panel" style="padding:0;overflow:hidden;">
      <div id="camp-list" style="padding:8px 4px;"></div>
    </div>
  `;
  const listHost = host.querySelector('#camp-list');
  mountSortableTable(listHost, {
    columns: [
      {
        key: 'title',
        label: 'Title',
        text: true,
        get: (r) => (r.title || '').toLowerCase(),
        cell: (r) => `
          <div style="font:600 14px 'IBM Plex Sans',sans-serif;color:var(--a-ink);">${escape(r.title)}</div>
          <div style="font:400 12px 'IBM Plex Mono',monospace;color:var(--a-faint);margin-top:3px;">${escape(r.subject)}</div>
        `,
      },
      {
        key: 'audience',
        label: 'Audience',
        text: true,
        get: (r) => r.audience_kind || '',
        cell: (r) => _renderAudienceBadge(r),
      },
      {
        key: 'recipient_count',
        label: 'Recipients',
        right: true,
        get: (r) => Number(r.recipient_count || 0),
        cell: (r) => escape(fmtInt(r.recipient_count)),
        className: 'mono',
      },
      {
        key: 'status',
        label: 'Status',
        text: true,
        get: (r) => r.status || '',
        cell: (r) => `<span class="adm2-chip ${_statusTone(r.status)}">${escape(r.status)}</span>`,
      },
      {
        key: 'sent_at',
        label: 'Sent',
        right: true,
        get: (r) => (r.sent_at ? new Date(r.sent_at).getTime() : 0),
        cell: (r) => escape(fmtRelTime(r.sent_at)),
      },
      {
        key: 'created_at',
        label: 'Created',
        right: true,
        get: (r) => (r.created_at ? new Date(r.created_at).getTime() : 0),
        cell: (r) => escape(fmtRelTime(r.created_at)),
      },
    ],
    rows: page.items,
    defaultSort: { key: 'created_at', dir: 'desc' },
    minWidth: 900,
    onRowClick: (row) => ctx.navigate(`/admin/v2/campaigns/${row.id}`),
    pageSizeKey: 'campaigns.list',
    emptyMessage: 'No campaigns yet. Click "New campaign" to compose one.',
  });
  host.querySelector('#camp-new').addEventListener('click', async () => {
    try {
      const draft = await api('/admin/campaigns', {
        method: 'POST',
        body: {
          title: 'Untitled campaign',
          subject: 'Come play the next Triviana trivia',
          intro_html: '',
          audience_kind: 'all_verified',
        },
      });
      ctx.navigate(`/admin/v2/campaigns/${draft.id}`);
    } catch (err) {
      toast.fromError(err);
    }
  });
}

function _renderAudienceBadge(campaign) {
  if (campaign.audience_kind === 'dormant') {
    const days = campaign.audience_dormant_days || 0;
    return `<span class="adm2-chip w">Dormant · ${escape(String(days))}d+</span>`;
  }
  return `<span class="adm2-chip">All verified</span>`;
}

function _statusTone(status) {
  if (status === 'sent') return 'g';
  if (status === 'failed') return 'r';
  if (status === 'sending') return 'w';
  return '';
}

// ---------------------------------------------------------------------------
// COMPOSE VIEW — form + preview + audience preview + (if sent) deliveries
// ---------------------------------------------------------------------------

const AUDIENCE_PAGE_SIZE = 25;

const _state = {
  campaign: null,
  audience_count: 0,
  upcoming_lives: [],
  audience_debounce: 0,
  // Audience preview pagination
  aud_offset: 0,
  aud_total: 0,
  aud_items: [],
  aud_page_size: AUDIENCE_PAGE_SIZE,
  // Custom recipient selection: `null` = disabled (send to full audience),
  // Set<user_id> = enabled (send only to the ids in the set).
  selection: null,
};

async function _renderComposer(host, ctx) {
  const [campaign, lives] = await Promise.all([
    api(`/admin/campaigns/${encodeURIComponent(ctx.campaignId)}`),
    api('/admin/campaigns/upcoming-lives'),
  ]);
  _state.campaign = campaign;
  _state.upcoming_lives = lives || [];
  _state.audience_count = 0;
  _state.aud_offset = 0;
  _state.aud_total = 0;
  _state.aud_items = [];
  _state.selection = null;
  host.innerHTML = _composerHtml(campaign);
  _wireComposer(host, ctx);
  await _refreshAudienceCount(host, campaign.id);
  await _refreshAudiencePreview(host, campaign.id);
  if (campaign.status === 'sent' || campaign.status === 'sending' || campaign.status === 'failed') {
    await _refreshDeliveries(host, campaign.id);
  }
}

function _composerHtml(c) {
  const editable = c.status === 'draft' || c.status === 'failed';
  const disabled = editable ? '' : 'disabled';
  const bannerBlock = c.banner_url
    ? `<div style="margin-top:12px;position:relative;">
         <img src="${escape(c.banner_url, true)}" alt="banner" style="max-width:100%;border-radius:10px;border:1px solid var(--a-border);"/>
         <button type="button" id="camp-banner-clear" class="adm2-btn" style="position:absolute;top:8px;right:8px;">Remove</button>
       </div>`
    : `<div style="margin-top:8px;font:500 12px 'IBM Plex Sans',sans-serif;color:var(--a-faint);">
         No banner uploaded yet. Click below to pick a PNG or JPG (≤ 8 MB).
       </div>`;

  const livesOpts = _state.upcoming_lives
    .map(
      (l) =>
        `<option value="${escape(l.id, true)}"${c.live_id === l.id ? ' selected' : ''}>${escape(l.title)} · ${escape(fmtKobo(l.prize_kobo))}</option>`,
    )
    .join('');

  const audienceKind = c.audience_kind || 'all_verified';
  const dormantDays = c.audience_dormant_days ?? 30;
  const showDeliveries =
    c.status === 'sent' || c.status === 'sending' || c.status === 'failed';

  return `
    <div style="display:flex;align-items:center;gap:12px;flex-wrap:wrap;margin-bottom:18px;">
      <a class="adm2-btn" href="/admin/v2/campaigns" data-link>‹ All campaigns</a>
      <span class="adm2-chip ${_statusTone(c.status)}">${escape(c.status)}</span>
      ${!editable ? '<span style="font:500 12px \'IBM Plex Sans\',sans-serif;color:var(--a-faint);">Read-only — this campaign has been sent.</span>' : ''}
    </div>

    <div class="adm2-two">
      <div class="adm2-panel">
        <div class="adm2-panel-title">Compose</div>
        <div class="adm2-panel-sub">All fields are saved as you go · draft mode</div>

        <label class="camp-lbl">Internal title</label>
        <input class="adm2-input camp-inp" id="camp-title" ${disabled} value="${escape(c.title, true)}" placeholder="Weekend dormant blast" />

        <label class="camp-lbl">Upcoming live trivia (optional)</label>
        <select class="adm2-input camp-inp" id="camp-live" ${disabled}>
          <option value="">— Free-form blast (no trivia link) —</option>
          ${livesOpts}
        </select>

        <label class="camp-lbl">Email subject</label>
        <input class="adm2-input camp-inp" id="camp-subject" ${disabled} value="${escape(c.subject, true)}" placeholder="Join tonight's ₦500,000 trivia" />

        <label class="camp-lbl">Intro paragraph (HTML allowed)</label>
        <textarea class="adm2-input camp-inp" id="camp-intro" ${disabled} rows="6" placeholder="&lt;p&gt;We noticed you haven't played in a while…&lt;/p&gt;">${escape(c.intro_html || '')}</textarea>

        <label class="camp-lbl">Banner image</label>
        ${bannerBlock}
        <div style="margin-top:8px;">
          <label class="adm2-btn" for="camp-banner-file" style="${editable ? '' : 'opacity:.4;cursor:not-allowed;pointer-events:none;'}">Upload banner</label>
          <input id="camp-banner-file" type="file" accept="image/*" style="display:none;" ${disabled} />
        </div>

        <label class="camp-lbl" style="margin-top:22px;">Audience</label>
        <div style="display:flex;gap:14px;flex-wrap:wrap;">
          <label style="display:flex;align-items:center;gap:8px;color:var(--a-ink-2);cursor:${editable ? 'pointer' : 'not-allowed'};">
            <input type="radio" name="camp-aud" value="all_verified" ${audienceKind === 'all_verified' ? 'checked' : ''} ${disabled} />
            All verified users
          </label>
          <label style="display:flex;align-items:center;gap:8px;color:var(--a-ink-2);cursor:${editable ? 'pointer' : 'not-allowed'};">
            <input type="radio" name="camp-aud" value="dormant" ${audienceKind === 'dormant' ? 'checked' : ''} ${disabled} />
            Dormant users
          </label>
        </div>
        <div id="camp-dormant-row" style="margin-top:10px;display:${audienceKind === 'dormant' ? 'flex' : 'none'};align-items:center;gap:10px;">
          <span style="font:500 12px 'IBM Plex Sans',sans-serif;color:var(--a-muted);">Inactive at least</span>
          <input class="adm2-input" type="number" id="camp-dormant-days" min="1" max="365" value="${escape(String(dormantDays))}" style="width:88px;" ${disabled} />
          <span style="font:500 12px 'IBM Plex Sans',sans-serif;color:var(--a-muted);">days</span>
        </div>

        <div style="margin-top:22px;padding:14px;background:var(--a-panel-2);border:1px solid var(--a-border);border-radius:10px;">
          <div style="font:600 11px 'IBM Plex Sans',sans-serif;color:var(--a-muted);letter-spacing:.05em;text-transform:uppercase;">Live audience count</div>
          <div id="camp-count" style="font:700 24px 'IBM Plex Mono',monospace;color:var(--a-ok);margin-top:6px;">…</div>
          <div style="font:500 11px 'IBM Plex Sans',sans-serif;color:var(--a-faint);margin-top:4px;">
            Excludes admins and blacklisted users.
          </div>
        </div>

        <div style="display:flex;gap:10px;margin-top:22px;flex-wrap:wrap;">
          <button type="button" id="camp-preview" class="adm2-btn">Preview email</button>
          <button type="button" id="camp-send" class="adm2-btn primary" ${editable ? '' : 'disabled'} style="${editable ? '' : 'opacity:.4;cursor:not-allowed;'}">Send now</button>
          <button type="button" id="camp-delete" class="adm2-btn" style="margin-left:auto;color:var(--a-bad);border-color:var(--a-bad-bd);" ${editable ? '' : 'disabled'}>Delete draft</button>
        </div>
      </div>

      <div class="adm2-panel">
        <div class="adm2-panel-title">Preview</div>
        <div class="adm2-panel-sub">Rendered exactly as the recipient sees it</div>
        <iframe id="camp-preview-frame" sandbox="allow-same-origin" style="width:100%;height:620px;border:1px solid var(--a-border);border-radius:10px;margin-top:14px;background:#0E0B1F;"></iframe>
      </div>
    </div>

    <div class="adm2-panel" style="margin-top:18px;">
      <div style="display:flex;align-items:center;justify-content:space-between;gap:12px;flex-wrap:wrap;">
        <div>
          <div class="adm2-panel-title">Preview recipients</div>
          <div class="adm2-panel-sub">Most dormant users first · check individual boxes to send only to the picks</div>
        </div>
        <label style="display:flex;align-items:center;gap:8px;color:var(--a-ink-2);font:600 12px 'IBM Plex Sans',sans-serif;cursor:${editable ? 'pointer' : 'not-allowed'};">
          <input type="checkbox" id="camp-select-mode" ${disabled} />
          Custom recipients
        </label>
      </div>
      <div id="camp-selection-badge" style="margin-top:10px;display:none;padding:10px 12px;background:var(--a-warm);border:1px solid var(--a-warm-bd);border-radius:9px;font:500 12px 'IBM Plex Mono',monospace;color:var(--a-warm-ink);">
        <span id="camp-selection-count">0</span> user(s) selected — send button will target this subset.
        <button type="button" id="camp-select-clear" class="adm2-btn" style="margin-left:12px;padding:2px 8px;">Clear</button>
      </div>
      <div id="camp-audience" style="margin-top:14px;overflow-x:auto;"></div>
      <div id="camp-audience-pager" style="display:flex;justify-content:space-between;align-items:center;padding-top:12px;border-top:1px solid var(--a-border);margin-top:8px;"></div>
    </div>

    ${showDeliveries ? _deliveriesShell() : ''}

    <style>
      .camp-lbl {
        display:block;font:600 11px 'IBM Plex Sans',sans-serif;
        letter-spacing:.05em;text-transform:uppercase;
        color:var(--a-muted);margin-top:18px;margin-bottom:6px;
      }
      .camp-inp { width:100%; box-sizing:border-box; }
      .camp-tbl { width:100%; border-collapse:collapse; }
      .camp-tbl th, .camp-tbl td {
        padding:9px 12px; text-align:left; border-bottom:1px solid var(--a-border);
        font:500 12px 'IBM Plex Sans',sans-serif;
      }
      .camp-tbl th {
        color:var(--a-muted); font-weight:600; letter-spacing:.04em;
        text-transform:uppercase; font-size:11px;
      }
      .camp-tbl td.right { text-align:right; }
      .camp-tbl td.mono, .camp-tbl th.mono { font-family:'IBM Plex Mono',monospace; }
      .camp-chip {
        display:inline-block; padding:2px 8px; border-radius:6px;
        font:600 10px 'IBM Plex Sans',sans-serif; letter-spacing:.03em;
        text-transform:uppercase;
      }
      .camp-chip.g { background:var(--a-ok-bg); color:var(--a-ok); }
      .camp-chip.r { background:var(--a-bad-bg); color:var(--a-bad); }
      .camp-chip.w { background:var(--a-warm); color:var(--a-warm-ink); }
    </style>
  `;
}

function _deliveriesShell() {
  return `
    <div class="adm2-panel" style="margin-top:18px;">
      <div class="adm2-panel-title">Deliveries &amp; open tracking</div>
      <div class="adm2-panel-sub">Per-recipient delivery + open telemetry · open counts reflect image loads in the recipient's mail client</div>
      <div id="camp-deliv-summary" style="margin-top:14px;display:flex;gap:10px;flex-wrap:wrap;"></div>
      <div id="camp-deliveries" style="margin-top:14px;overflow-x:auto;"></div>
      <div id="camp-deliveries-pager" style="display:flex;justify-content:space-between;align-items:center;padding-top:12px;border-top:1px solid var(--a-border);margin-top:8px;"></div>
    </div>
  `;
}

function _wireComposer(host, ctx) {
  const c = _state.campaign;
  const editable = c.status === 'draft' || c.status === 'failed';
  const debouncedPatch = _debounce(async (patch) => {
    try {
      const updated = await api(`/admin/campaigns/${encodeURIComponent(c.id)}`, {
        method: 'PATCH',
        body: patch,
      });
      _state.campaign = updated;
      _scheduleAudienceRefresh(host, c.id);
    } catch (err) {
      toast.fromError(err);
    }
  }, 400);

  if (editable) {
    host.querySelector('#camp-title').addEventListener('input', (e) => {
      debouncedPatch({ title: e.target.value });
    });
    host.querySelector('#camp-subject').addEventListener('input', (e) => {
      debouncedPatch({ subject: e.target.value });
    });
    host.querySelector('#camp-intro').addEventListener('input', (e) => {
      debouncedPatch({ intro_html: e.target.value });
    });
    host.querySelector('#camp-live').addEventListener('change', (e) => {
      debouncedPatch({ live_id: e.target.value || null });
    });

    host.querySelectorAll('input[name="camp-aud"]').forEach((r) => {
      r.addEventListener('change', () => {
        const val = r.value;
        const row = host.querySelector('#camp-dormant-row');
        row.style.display = val === 'dormant' ? 'flex' : 'none';
        const daysInput = host.querySelector('#camp-dormant-days');
        const days = Number(daysInput.value) || 30;
        const patch = { audience_kind: val };
        if (val === 'dormant') patch.audience_dormant_days = days;
        else patch.audience_dormant_days = null;
        debouncedPatch(patch);
      });
    });
    host.querySelector('#camp-dormant-days').addEventListener('input', (e) => {
      const val = Math.max(1, Math.min(365, Number(e.target.value) || 30));
      debouncedPatch({ audience_dormant_days: val });
    });

    const fileInput = host.querySelector('#camp-banner-file');
    fileInput.addEventListener('change', async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const fd = new FormData();
      fd.append('image', file);
      try {
        const res = await fetch(
          `${API_BASE}/admin/campaigns/${encodeURIComponent(c.id)}/banner`,
          {
            method: 'POST',
            headers: { Authorization: `Bearer ${auth.getAccessToken() || ''}` },
            body: fd,
            credentials: 'include',
          },
        );
        if (!res.ok) throw new ApiError(`Upload failed (${res.status})`, { status: res.status });
        toast.success('Banner uploaded.');
        await _refresh(host, ctx);
      } catch (err) {
        toast.fromError(err);
      }
    });

    const clearBtn = host.querySelector('#camp-banner-clear');
    if (clearBtn) {
      clearBtn.addEventListener('click', async () => {
        if (!window.confirm('Remove the banner?')) return;
        await api(`/admin/campaigns/${encodeURIComponent(c.id)}`, {
          method: 'PATCH',
          body: { banner_url: '' },
        });
        await _refresh(host, ctx);
      });
    }

    // Custom-recipient toggle
    const selectModeCb = host.querySelector('#camp-select-mode');
    selectModeCb.addEventListener('change', (e) => {
      _state.selection = e.target.checked ? new Set() : null;
      _updateSendButton(host);
      _renderAudienceTable(host);
      _renderSelectionBadge(host);
    });
    host.querySelector('#camp-select-clear').addEventListener('click', () => {
      _state.selection = new Set();
      _renderAudienceTable(host);
      _renderSelectionBadge(host);
      _updateSendButton(host);
    });

    host.querySelector('#camp-send').addEventListener('click', async () => {
      const usingSubset = _state.selection instanceof Set;
      const count = usingSubset ? _state.selection.size : _state.audience_count || 0;
      if (usingSubset && count === 0) {
        toast.error('Pick at least one recipient or turn off Custom recipients.');
        return;
      }
      const label = usingSubset
        ? `Send this campaign to the ${count.toLocaleString('en-NG')} selected user(s)?`
        : `Send this campaign to ${count.toLocaleString('en-NG')} users?`;
      if (!window.confirm(label)) return;
      try {
        const body = usingSubset ? { user_ids: Array.from(_state.selection) } : undefined;
        await api(`/admin/campaigns/${encodeURIComponent(c.id)}/send`, {
          method: 'POST',
          body,
        });
        toast.success('Send queued.');
        await _refresh(host, ctx);
      } catch (err) {
        toast.fromError(err);
      }
    });

    host.querySelector('#camp-delete').addEventListener('click', async () => {
      if (!window.confirm('Delete this draft?')) return;
      try {
        await api(`/admin/campaigns/${encodeURIComponent(c.id)}`, { method: 'DELETE' });
        toast.success('Draft deleted.');
        ctx.navigate('/admin/v2/campaigns');
      } catch (err) {
        toast.fromError(err);
      }
    });
  }

  host.querySelector('#camp-preview').addEventListener('click', () => _loadPreview(host, c.id));
  _loadPreview(host, c.id);
}

async function _loadPreview(host, campaignId) {
  try {
    const preview = await api(`/admin/campaigns/${encodeURIComponent(campaignId)}/preview`);
    const frame = host.querySelector('#camp-preview-frame');
    if (!frame) return;
    frame.srcdoc = preview.html;
  } catch (err) {
    toast.fromError(err);
  }
}

async function _refresh(host, ctx) {
  await _renderComposer(host, ctx);
}

function _scheduleAudienceRefresh(host, campaignId) {
  clearTimeout(_state.audience_debounce);
  _state.audience_debounce = setTimeout(() => {
    void _refreshAudienceCount(host, campaignId);
    _state.aud_offset = 0;
    void _refreshAudiencePreview(host, campaignId);
  }, 300);
}

async function _refreshAudienceCount(host, campaignId) {
  const el = host.querySelector('#camp-count');
  if (!el) return;
  try {
    const res = await api(`/admin/campaigns/${encodeURIComponent(campaignId)}/audience`);
    _state.audience_count = res.count || 0;
    el.textContent = fmtInt(_state.audience_count);
    _updateSendButton(host);
  } catch {
    el.textContent = '—';
  }
}

async function _refreshAudiencePreview(host, campaignId) {
  const listHost = host.querySelector('#camp-audience');
  const pager = host.querySelector('#camp-audience-pager');
  if (!listHost) return;
  listHost.innerHTML = `<div class="adm2-skl" style="height:180px;border-radius:8px;"></div>`;
  try {
    const res = await api(
      `/admin/campaigns/${encodeURIComponent(campaignId)}/audience/preview?limit=${_state.aud_page_size}&offset=${_state.aud_offset}`,
    );
    _state.aud_items = res.items || [];
    _state.aud_total = res.total || 0;
    _renderAudienceTable(host);
    pager.innerHTML = _renderAudiencePagerHtml();
    _wireAudiencePager(host, campaignId);
  } catch (err) {
    listHost.innerHTML = errorState(err);
  }
}

function _renderAudienceTable(host) {
  const listHost = host.querySelector('#camp-audience');
  if (!listHost) return;
  const items = _state.aud_items || [];
  if (!items.length) {
    listHost.innerHTML = `<div style="padding:24px 12px;text-align:center;color:var(--a-faint);">No users match the current filter.</div>`;
    return;
  }
  const selection = _state.selection;
  const showCheckbox = selection instanceof Set;
  const rowsHtml = items
    .map((u) => {
      const dormancy =
        u.days_since_last_seen == null
          ? `<span class="camp-chip w">Never</span>`
          : u.days_since_last_seen >= 30
            ? `<span class="camp-chip w">${escape(String(u.days_since_last_seen))}d</span>`
            : `<span style="color:var(--a-faint);">${escape(String(u.days_since_last_seen))}d</span>`;
      const checked = showCheckbox && selection.has(u.id) ? 'checked' : '';
      const cbCell = showCheckbox
        ? `<td style="width:32px;"><input type="checkbox" data-user-cb="${escape(u.id, true)}" ${checked}/></td>`
        : '';
      return `
        <tr>
          ${cbCell}
          <td>
            <div style="font:600 13px 'IBM Plex Sans',sans-serif;color:var(--a-ink);">${escape(u.email)}</div>
            <div style="font:400 11px 'IBM Plex Mono',monospace;color:var(--a-faint);margin-top:2px;">${escape(u.username ? '@' + u.username : '—')}</div>
          </td>
          <td class="mono right">${escape(fmtRelTime(u.last_seen_at))}</td>
          <td class="right">${dormancy}</td>
        </tr>
      `;
    })
    .join('');
  const headerCb = showCheckbox
    ? `<th style="width:32px;"><input type="checkbox" id="camp-select-page" title="Select all on this page"/></th>`
    : '';
  listHost.innerHTML = `
    <table class="camp-tbl" style="min-width:640px;">
      <thead>
        <tr>
          ${headerCb}
          <th>User</th>
          <th class="right">Last seen</th>
          <th class="right">Dormancy</th>
        </tr>
      </thead>
      <tbody>${rowsHtml}</tbody>
    </table>
  `;
  if (showCheckbox) {
    listHost.querySelectorAll('[data-user-cb]').forEach((cb) => {
      cb.addEventListener('change', (e) => {
        const uid = e.target.dataset.userCb;
        if (e.target.checked) _state.selection.add(uid);
        else _state.selection.delete(uid);
        _renderSelectionBadge(host);
        _updateSendButton(host);
      });
    });
    const pageAll = listHost.querySelector('#camp-select-page');
    // Reflect current state of page: checked only if every visible row is in the set.
    const allSelected = items.every((u) => _state.selection.has(u.id));
    pageAll.checked = allSelected;
    pageAll.addEventListener('change', (e) => {
      const shouldSelect = e.target.checked;
      items.forEach((u) => {
        if (shouldSelect) _state.selection.add(u.id);
        else _state.selection.delete(u.id);
      });
      _renderAudienceTable(host);
      _renderSelectionBadge(host);
      _updateSendButton(host);
    });
  }
}

function _renderSelectionBadge(host) {
  const badge = host.querySelector('#camp-selection-badge');
  const count = host.querySelector('#camp-selection-count');
  if (!badge || !count) return;
  if (_state.selection instanceof Set) {
    badge.style.display = 'block';
    count.textContent = fmtInt(_state.selection.size);
  } else {
    badge.style.display = 'none';
  }
}

function _updateSendButton(host) {
  const btn = host.querySelector('#camp-send');
  if (!btn) return;
  if (_state.selection instanceof Set) {
    const n = _state.selection.size;
    btn.textContent = n > 0 ? `Send to ${fmtInt(n)} selected` : 'Pick at least one recipient';
  } else {
    btn.textContent = 'Send now';
  }
}

function _renderAudiencePagerHtml() {
  const total = _state.aud_total || 0;
  const size = _state.aud_page_size;
  const start = total ? _state.aud_offset + 1 : 0;
  const end = Math.min(total, _state.aud_offset + _state.aud_items.length);
  const onFirst = _state.aud_offset === 0;
  const onLast = end >= total;
  return `
    <button type="button" class="adm2-btn" id="camp-aud-prev" ${onFirst ? 'disabled style="opacity:.4;cursor:not-allowed;"' : ''}>← Prev</button>
    <div style="font:500 11px 'IBM Plex Mono',monospace;color:var(--a-muted);">${fmtInt(start)}–${fmtInt(end)} of ${fmtInt(total)}</div>
    <button type="button" class="adm2-btn" id="camp-aud-next" ${onLast ? 'disabled style="opacity:.4;cursor:not-allowed;"' : ''}>Next →</button>
  `;
}

function _wireAudiencePager(host, campaignId) {
  const prev = host.querySelector('#camp-aud-prev');
  const next = host.querySelector('#camp-aud-next');
  if (prev)
    prev.addEventListener('click', () => {
      _state.aud_offset = Math.max(0, _state.aud_offset - _state.aud_page_size);
      void _refreshAudiencePreview(host, campaignId);
    });
  if (next)
    next.addEventListener('click', () => {
      _state.aud_offset += _state.aud_page_size;
      void _refreshAudiencePreview(host, campaignId);
    });
}

// ---------------------------------------------------------------------------
// Deliveries (sent campaigns)
// ---------------------------------------------------------------------------

const _deliveries = {
  offset: 0,
  page_size: 50,
  total: 0,
  items: [],
  sent: 0,
  failed: 0,
  opened: 0,
};

async function _refreshDeliveries(host, campaignId) {
  const listHost = host.querySelector('#camp-deliveries');
  const summary = host.querySelector('#camp-deliv-summary');
  const pager = host.querySelector('#camp-deliveries-pager');
  if (!listHost) return;
  listHost.innerHTML = `<div class="adm2-skl" style="height:200px;border-radius:8px;"></div>`;
  try {
    const res = await api(
      `/admin/campaigns/${encodeURIComponent(campaignId)}/deliveries?limit=${_deliveries.page_size}&offset=${_deliveries.offset}`,
    );
    _deliveries.items = res.items || [];
    _deliveries.total = res.total || 0;
    _deliveries.sent = res.sent_count || 0;
    _deliveries.failed = res.failed_count || 0;
    _deliveries.opened = res.opened_count || 0;
    summary.innerHTML = _renderDeliverySummary();
    listHost.innerHTML = _renderDeliveryTable();
    pager.innerHTML = _renderDeliveryPagerHtml();
    _wireDeliveryPager(host, campaignId);
  } catch (err) {
    listHost.innerHTML = errorState(err);
  }
}

function _renderDeliverySummary() {
  const openRate =
    _deliveries.sent > 0 ? Math.round((100 * _deliveries.opened) / _deliveries.sent) : 0;
  const chip = (label, value, tone) => `
    <div style="padding:10px 14px;border:1px solid var(--a-border);border-radius:9px;background:var(--a-panel-2);">
      <div style="font:600 10px 'IBM Plex Sans',sans-serif;color:var(--a-muted);letter-spacing:.05em;text-transform:uppercase;">${escape(label)}</div>
      <div style="font:700 22px 'IBM Plex Mono',monospace;color:${tone};margin-top:6px;">${escape(String(value))}</div>
    </div>
  `;
  return `
    ${chip('Sent', fmtInt(_deliveries.sent), 'var(--a-ok)')}
    ${chip('Failed', fmtInt(_deliveries.failed), _deliveries.failed ? 'var(--a-bad)' : 'var(--a-faint)')}
    ${chip('Opened', fmtInt(_deliveries.opened), 'var(--a-warm-ink)')}
    ${chip('Open rate', `${openRate}%`, 'var(--a-warm-ink)')}
  `;
}

function _renderDeliveryTable() {
  const items = _deliveries.items;
  if (!items.length) {
    return `<div style="padding:24px 12px;text-align:center;color:var(--a-faint);">No deliveries recorded yet.</div>`;
  }
  const rowsHtml = items
    .map((d) => {
      const statusChip =
        d.status === 'sent'
          ? '<span class="camp-chip g">SENT</span>'
          : '<span class="camp-chip r">FAILED</span>';
      const openChip =
        d.opened_at != null
          ? `<span class="camp-chip w">Opened ×${escape(String(d.open_count || 1))}</span>`
          : '<span style="color:var(--a-faint);">—</span>';
      return `
        <tr>
          <td>
            <div style="font:600 13px 'IBM Plex Sans',sans-serif;color:var(--a-ink);">${escape(d.email)}</div>
            <div style="font:400 11px 'IBM Plex Mono',monospace;color:var(--a-faint);margin-top:2px;">${escape(d.username ? '@' + d.username : (d.user_id || '—').slice(0, 8))}</div>
          </td>
          <td>${statusChip}</td>
          <td class="mono right">${escape(fmtRelTime(d.sent_at))}</td>
          <td class="right">${openChip}</td>
          <td class="mono right">${escape(fmtRelTime(d.opened_at))}</td>
          <td>${d.error ? `<span style="color:var(--a-bad);font:400 11px 'IBM Plex Sans',sans-serif;">${escape(d.error)}</span>` : ''}</td>
        </tr>
      `;
    })
    .join('');
  return `
    <table class="camp-tbl" style="min-width:820px;">
      <thead>
        <tr>
          <th>Recipient</th>
          <th>Status</th>
          <th class="right">Sent</th>
          <th class="right">Opens</th>
          <th class="right">First open</th>
          <th>Error</th>
        </tr>
      </thead>
      <tbody>${rowsHtml}</tbody>
    </table>
  `;
}

function _renderDeliveryPagerHtml() {
  const total = _deliveries.total || 0;
  const start = total ? _deliveries.offset + 1 : 0;
  const end = Math.min(total, _deliveries.offset + _deliveries.items.length);
  const onFirst = _deliveries.offset === 0;
  const onLast = end >= total;
  return `
    <button type="button" class="adm2-btn" id="camp-deliv-prev" ${onFirst ? 'disabled style="opacity:.4;cursor:not-allowed;"' : ''}>← Prev</button>
    <div style="font:500 11px 'IBM Plex Mono',monospace;color:var(--a-muted);">${fmtInt(start)}–${fmtInt(end)} of ${fmtInt(total)}</div>
    <button type="button" class="adm2-btn" id="camp-deliv-next" ${onLast ? 'disabled style="opacity:.4;cursor:not-allowed;"' : ''}>Next →</button>
  `;
}

function _wireDeliveryPager(host, campaignId) {
  const prev = host.querySelector('#camp-deliv-prev');
  const next = host.querySelector('#camp-deliv-next');
  if (prev)
    prev.addEventListener('click', () => {
      _deliveries.offset = Math.max(0, _deliveries.offset - _deliveries.page_size);
      void _refreshDeliveries(host, campaignId);
    });
  if (next)
    next.addEventListener('click', () => {
      _deliveries.offset += _deliveries.page_size;
      void _refreshDeliveries(host, campaignId);
    });
}

function _debounce(fn, ms) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), ms);
  };
}
